import { useCallback, useEffect, useId, useLayoutEffect, useMemo, useRef, useState } from 'react';
import { Link, useNavigate, useParams } from 'react-router-dom';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { faSpinner } from '@fortawesome/free-solid-svg-icons';
import { Ellipsis } from 'lucide-react';
import { useTheme } from '@course-authoring/shared-types';
import { useAuth } from '../../auth';
import { useClientAccounts } from '../../api/accountAssignmentApi';
import {
  AuthorWithAiApiError,
  isPart,
  type BuildAction,
  type BuildView,
  type MessageView,
  type OutlineSnapshotPart,
} from '../../api/authorWithAiApi';
import { useBuildView, usePostAction, usePostMessage } from '../../api/authorWithAiQueries';
import { ModalShell } from '../../components/ModalShell';
import { QuickSelectChips, type ChipOption } from '../../components/QuickSelectChips';
import {
  BuildFailureNotice,
  EarlierDrafts,
  GenerationMessage,
  NeedsInputNote,
  TurnFailureNotice,
  WaitingCard,
} from '../../components/authorWithAi/ConversationStatus';
import { MessageItem } from '../../components/authorWithAi/MessageParts';
import { Composer } from '../../components/authorWithAi/Composer';
import { previousSnapshots } from '../../components/authorWithAi/outlineDiff';
import { rowErrorsForPick } from '../../components/authorWithAi/pickerErrors';
import { QuestionBankPicker } from '../../components/authorWithAi/QuestionBankPicker';
import { VideoPicker } from '../../components/authorWithAi/VideoPicker';
import {
  activeTurn,
  composerState,
  deriveChips,
  failedTurn,
  isSnapshotCurrent,
  latestAssistantMessage,
  latestGenerationRun,
  type ChipSpec,
} from '../../components/authorWithAi/threadModel';
import { useLibraryLabels, type LibraryRef } from '../../components/authorWithAi/useLibraryLabels';
import { BADGES, COMPOSER_COPY, CONVERSATION_COPY, SEARCH_LINK_LEAD } from '../../components/authorWithAi/authorWithAiCopy';
import { createAuthorWithAiStyles } from '../../components/authorWithAi/authorWithAiStyles';
import { AuthorWithAiGate } from './AuthorWithAiGate';

/** Distance from the bottom within which new messages scroll into view. */
const NEAR_BOTTOM_PX = 120;

function initialsOf(name: string | null | undefined): string {
  const initials = (name ?? '').split(' ').filter(Boolean).slice(0, 2).map((part) => part[0]?.toUpperCase() ?? '').join('');
  return initials || 'U';
}

/** Library items the thread needs labels for: selection tiles and reference-backed outline rows. */
function libraryRefs(view: BuildView): LibraryRef[] {
  const refs: LibraryRef[] = view.workingState.selections.map((selection) => ({ kind: selection.kind, libraryId: selection.libraryId }));
  for (const message of view.messages) {
    for (const part of message.parts) {
      if (isPart(part, 'selection_change')) {
        const kind = part.decision === 'video' ? 'video' as const : 'question' as const;
        for (const libraryId of part.libraryIds) refs.push({ kind, libraryId });
      }
    }
  }
  return refs;
}

export function AuthorWithAiConversationPage() {
  return <AuthorWithAiGate>{() => <Conversation />}</AuthorWithAiGate>;
}

function Conversation() {
  const { buildId = '' } = useParams();
  const { tokens } = useTheme();
  const styles = useMemo(() => createAuthorWithAiStyles(tokens), [tokens]);
  const query = useBuildView(buildId);

  if (query.isPending) {
    return (
      <div style={styles.centered} role="status" aria-label="Loading">
        <FontAwesomeIcon icon={faSpinner} spin aria-hidden="true" />
      </div>
    );
  }
  if (query.isError) {
    const notFound = query.error instanceof AuthorWithAiApiError && query.error.status === 404;
    return (
      <div style={styles.centered}>
        <h1 style={styles.heading}>{notFound ? CONVERSATION_COPY.notFoundHeading : CONVERSATION_COPY.loadError}</h1>
        {notFound && <p style={styles.muted}>{CONVERSATION_COPY.notFoundBody}</p>}
        <Link to="/author-with-ai" style={styles.backLink}>{CONVERSATION_COPY.notFoundLink}</Link>
      </div>
    );
  }
  return <ConversationScreen view={query.data} />;
}

function ConversationScreen({ view }: { view: BuildView }) {
  const { tokens } = useTheme();
  const styles = useMemo(() => createAuthorWithAiStyles(tokens), [tokens]);
  const navigate = useNavigate();
  const { user } = useAuth();
  const accounts = useClientAccounts();
  const postMessage = usePostMessage(view.id);
  const postAction = usePostAction(view.id);
  const discardTitleId = useId();

  const [draft, setDraft] = useState('');
  const [composerError, setComposerError] = useState<string | null>(null);
  const [actionError, setActionError] = useState<string | null>(null);
  const [revealedFor, setRevealedFor] = useState<string | null>(null);
  const [staleNotice, setStaleNotice] = useState(false);
  const [menuOpen, setMenuOpen] = useState(false);
  const [discardOpen, setDiscardOpen] = useState(false);
  const [bankPickerDecision, setBankPickerDecision] = useState<'anchor_question' | 'additional_questions'>('anchor_question');
  const [bankPickerOpen, setBankPickerOpen] = useState(false);
  const [videoPickerOpen, setVideoPickerOpen] = useState(false);
  const [pickerErrors, setPickerErrors] = useState<Record<string, string>>({});
  const [listErrors, setListErrors] = useState<Record<string, string>>({});

  const threadRef = useRef<HTMLDivElement | null>(null);
  const latestBoxRef = useRef<HTMLDivElement | null>(null);
  const nearBottomRef = useRef(true);
  const scrolledOnce = useRef(false);

  const inFlight = postMessage.isPending || postAction.isPending;
  const composer = composerState(view);
  const accountName = view.accountId === null
    ? null
    : accounts.data?.find((account) => account.id === String(view.accountId))?.name ?? null;

  const labelFor = useLibraryLabels(view.id, useMemo(() => libraryRefs(view), [view]));

  // Pair every outline snapshot with the earlier snapshot of the same target.
  // Controls go on the latest box that shows the current plan; a reply
  // recovered after later input appends an older snapshot that must not take them.
  const snapshotIndex = useMemo(() => {
    const snapshots = view.messages.flatMap((message) => message.parts
      .filter((part): part is OutlineSnapshotPart => isPart(part, 'outline_snapshot'))
      .map((part) => ({ messageId: message.id, part })));
    const current = [...snapshots].reverse().find((entry) => isSnapshotCurrent(view, entry.part));
    return {
      previous: previousSnapshots(snapshots),
      latestMessageId: (current ?? snapshots[snapshots.length - 1])?.messageId ?? null,
    };
  }, [view]);

  const lastAssistant = latestAssistantMessage(view.messages);
  const lastMessage = view.messages[view.messages.length - 1] ?? null;
  const chipsVisible = composer.enabled && lastAssistant !== null && lastMessage?.id === lastAssistant.id;
  const chips: ChipSpec[] = chipsVisible && lastAssistant
    ? deriveChips(view, lastAssistant, { reviewModesRevealed: revealedFor === lastAssistant.id })
    : [];

  // Keep the reader at the bottom when new messages arrive, unless they scrolled up.
  useLayoutEffect(() => {
    const thread = threadRef.current;
    if (!thread) return;
    if (!scrolledOnce.current || nearBottomRef.current) {
      thread.scrollTop = thread.scrollHeight;
      scrolledOnce.current = true;
    }
  }, [view.messages.length, view.status, view.execution.active?.id]);

  const onThreadScroll = () => {
    const thread = threadRef.current;
    if (!thread) return;
    nearBottomRef.current = thread.scrollHeight - thread.scrollTop - thread.clientHeight < NEAR_BOTTOM_PX;
  };

  useEffect(() => {
    setRevealedFor(null);
    setListErrors({});
  }, [view.messages.length]);

  const describeError = (error: unknown): string => {
    if (!(error instanceof AuthorWithAiApiError)) return error instanceof Error ? error.message : COMPOSER_COPY.conflict;
    if (error.code === 'busy') return COMPOSER_COPY.busy;
    if (error.code === 'idempotency_conflict' || (error.status === 409 && error.code !== 'invalid_state')) return COMPOSER_COPY.conflict;
    return error.message;
  };

  const sendText = (text: string) => {
    setComposerError(null);
    setActionError(null);
    postMessage.mutate(text, {
      onSuccess: () => setDraft((current) => (current.trim() === text ? '' : current)),
      onError: (error) => setComposerError(describeError(error)),
    });
  };

  /** Resolves to the error (already shown, unless it belongs on a picker row) or null. */
  const submitAction = useCallback(async (action: BuildAction): Promise<unknown> => {
    setActionError(null);
    setStaleNotice(false);
    try {
      await postAction.mutateAsync(action);
      if (action.type === 'discard') navigate('/author-with-ai');
      return null;
    } catch (error) {
      if (action.type === 'approve' && error instanceof AuthorWithAiApiError && error.code === 'invalid_state') {
        setStaleNotice(true);
        latestBoxRef.current?.scrollIntoView?.({ behavior: 'smooth', block: 'center' });
        return error;
      }
      const onPickerRow = action.type === 'select' && error instanceof AuthorWithAiApiError && error.status === 422;
      if (!onPickerRow) setActionError(describeError(error));
      return error;
    }
  }, [postAction, navigate]);

  /**
   * Selects a question or the video from an inline list or its picker; a
   * refusal shows on the picked row, where the author is looking (the page's
   * banner sits behind an open picker), whether or not the server named a row.
   */
  const pickLibraryItem = async (
    decision: 'anchor_question' | 'additional_questions' | 'video',
    libraryIds: string[],
    showRowErrors: (rows: Record<string, string>) => void,
  ): Promise<boolean> => {
    showRowErrors({});
    const error = await submitAction({ type: 'select', decision, libraryIds });
    if (!error) return true;
    showRowErrors(rowErrorsForPick(error, libraryIds, describeError(error)));
    setActionError(null);
    return false;
  };
  const pickAnchor = (libraryIds: string[], showRowErrors: (rows: Record<string, string>) => void) =>
    pickLibraryItem('anchor_question', libraryIds, showRowErrors);
  const pickVideo = (libraryId: string, showRowErrors: (rows: Record<string, string>) => void) =>
    pickLibraryItem('video', [libraryId], showRowErrors);

  const openPicker = (picker: 'question' | 'video', decision: 'anchor_question' | 'additional_questions' = 'anchor_question') => {
    setPickerErrors({});
    if (picker === 'video') setVideoPickerOpen(true);
    else { setBankPickerDecision(decision); setBankPickerOpen(true); }
  };
  const onChip = (value: string) => {
    const chip = chips[Number(value)];
    if (!chip) return;
    if (chip.kind === 'suggestion') sendText(chip.label);
    else if (chip.kind === 'action' || chip.kind === 'option') void submitAction(chip.action);
    else if (chip.kind === 'picker') openPicker(chip.picker, chip.decision);
    else if (chip.kind === 'reveal-review-modes' && lastAssistant) setRevealedFor(lastAssistant.id);
  };

  // The whole-library search is not one of the answers, so it follows the
  // chips as a link ("or search all questions") instead of being a chip.
  const chipOptions: ChipOption[] = chips.flatMap((chip, index) => (chip.kind === 'picker' && chip.style === 'link'
    ? []
    : [{ label: chip.label, value: String(index), ...(chip.kind === 'action' && chip.primary ? { primary: true } : {}) }]));
  const searchLink = chips.find((chip): chip is Extract<ChipSpec, { kind: 'picker' }> => chip.kind === 'picker' && chip.style === 'link') ?? null;
  const searchLinkLabel = searchLink
    ? (chipOptions.length > 0 ? searchLink.label : searchLink.label.charAt(0).toUpperCase() + searchLink.label.slice(1))
    : '';
  const chipsShown = chipOptions.length > 0 || searchLink !== null;

  const generation = latestGenerationRun(view);
  const turn = activeTurn(view);
  const failed = turn ? null : failedTurn(view);
  const latestRun = view.execution.recent[0] ?? null;
  const canDiscard = view.allowedActions.some((action) => action.type === 'discard')
    && (view.status === 'failed' || (view.status === 'planning' && !view.execution.active));

  const footer = composerError ?? (composer.enabled ? null : composer.reason);

  return (
    <div style={styles.screen}>
      <header style={styles.screenHeader}>
        <div style={styles.titleGroup}>
          <h1 style={styles.screenTitle}>{view.title}</h1>
        </div>
        {view.status === 'discarded' && <span style={styles.badge}>{BADGES.discarded}</span>}
        {view.status === 'completed' && <span style={{ ...styles.badge, ...styles.badgePublished }}>{BADGES.completed}</span>}
        {canDiscard && (
          <div style={styles.menu}>
            <button type="button" style={styles.iconButton} aria-label={CONVERSATION_COPY.menuLabel} aria-expanded={menuOpen} onClick={() => setMenuOpen((open) => !open)}>
              <Ellipsis size={18} strokeWidth={1.75} aria-hidden="true" />
            </button>
            {menuOpen && (
              <div style={styles.menuPopover} role="menu">
                <button type="button" role="menuitem" style={styles.menuItem} onClick={() => { setMenuOpen(false); setDiscardOpen(true); }}>
                  {CONVERSATION_COPY.discard}
                </button>
              </div>
            )}
          </div>
        )}
      </header>

      <div style={styles.threadBack}>
        <Link to="/author-with-ai" style={styles.backLink}>{CONVERSATION_COPY.back}</Link>
      </div>
      <div ref={threadRef} style={styles.thread} onScroll={onThreadScroll} data-testid="thread">
        <div style={styles.threadInner}>
          <div style={styles.threadSpacer} />
          {view.messages.map((message: MessageView) => (
            <MessageItem
              key={message.id}
              message={message}
              view={view}
              initials={initialsOf(user?.name)}
              labelFor={labelFor}
              accountName={accountName}
              previousSnapshot={snapshotIndex.previous.get(message.id) ?? null}
              {...(message.id === snapshotIndex.latestMessageId ? { latestBox: { staleNotice }, boxRef: latestBoxRef } : {})}
              {...(chipsVisible && message.id === lastAssistant?.id ? {
                questionList: { onPick: (libraryId: string) => void pickAnchor([libraryId], setListErrors), disabled: inFlight, rowErrors: listErrors },
                scenarioList: { onPick: (libraryId: string) => void pickVideo(libraryId, setListErrors), disabled: inFlight, rowErrors: listErrors },
              } : {})}
            />
          ))}

          {turn && <WaitingCard view={view} run={turn} />}
          {failed && <TurnFailureNotice run={failed.run} retry={failed.retry} onSubmit={submitAction} disabled={inFlight} />}
          {view.status === 'building' && view.progress && <GenerationMessage progress={view.progress} />}
          {view.status === 'failed' && generation && (generation.status === 'failed' || generation.status === 'needs_input') && (
            <BuildFailureNotice view={view} run={generation} onSubmit={submitAction} onDiscard={() => setDiscardOpen(true)} disabled={inFlight} />
          )}
          {view.status === 'planning' && latestRun?.kind === 'generation' && latestRun.status === 'needs_input' && <NeedsInputNote view={view} />}
          <EarlierDrafts view={view} />

          {chipsShown && (
            <div style={{ paddingLeft: '48px', marginTop: '-12px' }} data-testid="chips">
              <QuickSelectChips
                variant="admin"
                chips={chipOptions}
                onSelect={onChip}
                disabled={inFlight}
                groupLabel="Reply options"
                chipAriaLabel={(chip) => chip.label}
                trailing={searchLink && (
                  <span>
                    {chipOptions.length > 0 && <span style={styles.searchLinkLead}>{SEARCH_LINK_LEAD} </span>}
                    <button type="button" style={styles.searchLink} onClick={() => openPicker(searchLink.picker)} disabled={inFlight}>
                      {searchLinkLabel}
                    </button>
                  </span>
                )}
              />
            </div>
          )}
          {actionError && <div style={styles.errorBanner} role="alert">{actionError}</div>}
        </div>
      </div>

      <Composer
        value={draft}
        onChange={setDraft}
        onSend={sendText}
        enabled={composer.enabled}
        sending={inFlight}
        chipsShown={chipsShown}
        footer={footer}
        footerIsError={composerError !== null && composerError !== COMPOSER_COPY.busy}
        finished={view.status === 'completed'}
      />

      {/* Both question decisions open the Question Bank page in a dialog, one pick each (Sara, 2026-11-04: the picker is single-select). */}
      <QuestionBankPicker
        buildId={view.id}
        isOpen={bankPickerOpen}
        onClose={() => setBankPickerOpen(false)}
        onPick={(libraryIds) => void pickLibraryItem(bankPickerDecision, libraryIds, setPickerErrors).then((picked) => { if (picked) setBankPickerOpen(false); })}
        rowErrors={pickerErrors}
        busy={inFlight}
      />

      <VideoPicker
        isOpen={videoPickerOpen}
        onClose={() => setVideoPickerOpen(false)}
        onPick={(libraryId) => void pickVideo(libraryId, setPickerErrors).then((picked) => { if (picked) setVideoPickerOpen(false); })}
        rowErrors={pickerErrors}
        busy={inFlight}
      />

      <ModalShell isOpen={discardOpen} onClose={() => setDiscardOpen(false)} ariaLabelledBy={discardTitleId} width="440px" presentation="admin">
        <div style={styles.modalHeader}>
          <h2 id={discardTitleId} style={styles.subheading}>{CONVERSATION_COPY.discardTitle}</h2>
        </div>
        <div style={styles.modalBody}>
          <p style={styles.paragraph}>{CONVERSATION_COPY.discardBody}</p>
        </div>
        <div style={styles.modalFooter}>
          <button type="button" style={styles.button} onClick={() => setDiscardOpen(false)}>{CONVERSATION_COPY.cancel}</button>
          <button
            type="button"
            style={styles.dangerButton}
            disabled={inFlight}
            onClick={() => { setDiscardOpen(false); void submitAction({ type: 'discard' }); }}
          >
            {CONVERSATION_COPY.discardConfirm}
          </button>
        </div>
      </ModalShell>
    </div>
  );
}
